import { useNavigate } from "react-router";
import { z } from "zod";
import { MODELS, MUTATIONS, useModel, useMutation } from "../../../api";
import type { WorkshopSettings, WorkshopStepInfo } from "../../../api/workshop";
import PageShell from "../../../design-system/components/PageShell/PageShell";
import { useConfirmAction } from "../../../design-system/primitives/components/AlertDialog/useConfirmAction";
import Button from "../../../design-system/primitives/components/Button/Button";
import Checkbox from "../../../design-system/primitives/components/Checkbox/Checkbox";
import Divider from "../../../design-system/primitives/components/Divider/Divider";
import Select from "../../../design-system/primitives/components/Select/Select";
import Text from "../../../design-system/primitives/components/Text/Text";
import NotAuthorizedPage from "../../auth/UnauthorizedView/NotAuthorizedPage.jsx";
import { canManageWorkshop } from "../../../lib/Roles";
import { ROUTES } from "../../../routes.js";
import type { SparkBag } from "../sparkBag";
import styles from "./optionsOverview.css";

type ProjectSortMode = WorkshopSettings["projectSortMode"];

/** The settings fields this page can write; each control sends exactly one. */
type SettingsChange = Partial<
  Pick<
    WorkshopSettings,
    "libraryEnabled" | "commentsEnabled" | "projectSortMode"
  >
>;

/**
 * The sort modes as a runtime schema, so the string a Select hands back can be
 * narrowed by parsing it rather than by asserting it. The two declarations
 * below pin each other: a mode missing from `SORT_LABELS` fails the `Record`,
 * and a mode in the schema that the API doesn't know fails the lookup.
 */
const projectSortModes = z.enum(["creation_date", "random"]);

const SORT_LABELS: Record<ProjectSortMode, string> = {
  creation_date: "aufsteigend nach Erstellungsdatum",
  random: "Zufällige Reihenfolge",
};

const sortOptions = projectSortModes.options.map((value) => ({
  value,
  label: SORT_LABELS[value],
}));

/**
 * Sends the user back to the spark overview with the tour requested, which is
 * where the tour's own closing step tells them to look for it.
 */
const RestartTourButton = ({ workshop }: { workshop: WorkshopStepInfo }) => {
  const navigate = useNavigate();

  return (
    <Button
      title="Tour durch die Lernreise erneut starten"
      size="large"
      color="accent"
      onClick={() =>
        void navigate(
          ROUTES.spark.getUrl({
            workshopSlug: workshop.slug,
            sparkSlug: workshop.spark.slug,
            withTour: true,
          }),
        )
      }
    />
  );
};

/** The workshop's own settings, one control per field. */
const SettingsSection = ({
  workshop,
  settings,
}: {
  workshop: WorkshopStepInfo;
  settings: WorkshopSettings;
}) => {
  const [doUpdateWorkshopSettings] = useMutation(
    MUTATIONS.updateWorkshopSettings,
  );

  // Every control writes one field of the same settings record, so they share
  // the ids the mutation needs to revalidate it.
  const updateSettings = (changed: SettingsChange) =>
    doUpdateWorkshopSettings({
      workshopId: workshop.id,
      slug: workshop.slug,
      ...changed,
    });

  return (
    <section className={styles.section}>
      <Text variant="subheader-small">Lernreise-Einstellungen</Text>

      <Checkbox
        name="libraryEnabled"
        label="Bibliothek aktivieren"
        checked={settings.libraryEnabled}
        // Radix reports a tri-state; only a real tick enables the library.
        onCheckedChange={(checked) =>
          void updateSettings({ libraryEnabled: checked === true })
        }
      />

      <Checkbox
        name="commentsEnabled"
        label="Kommentare im Showroom aktivieren"
        checked={settings.commentsEnabled}
        onCheckedChange={(checked) =>
          void updateSettings({ commentsEnabled: checked === true })
        }
      />

      <Select
        label="Sortierung der Projekte im Showroom"
        name="projectSortMode"
        options={sortOptions}
        value={settings.projectSortMode}
        onValueChange={(value) =>
          void updateSettings({
            projectSortMode: projectSortModes.parse(value),
          })
        }
      />
    </section>
  );
};

/**
 * Deleting the workshop, behind a destructive confirmation. Renders nothing at
 * all for anyone who may not delete it — including the rule above it, which
 * would otherwise sit under the last setting with no section to separate.
 */
const DeleteSection = ({ workshop }: { workshop: WorkshopStepInfo }) => {
  const navigate = useNavigate();
  const me = useModel(MODELS.ME);
  const [doDeleteWorkshop] = useMutation(MUTATIONS.deleteWorkshop);

  // Leaving is part of the confirmed action, so it happens only once the
  // delete has actually succeeded — a rejection keeps the user on the page
  // with the dialog's error message instead.
  const deleteWorkshop = useConfirmAction({
    onConfirm: () =>
      doDeleteWorkshop({ id: workshop.id })?.then(() => {
        void navigate(ROUTES.lernreisenOverview.getUrl());
      }),
    variant: "destructive",
    title: "Lernreise löschen?",
    description:
      "Wenn du die Lernreise löschst, gehen alle darin gespeicherten Informationen verloren.",
    errorDescription:
      "Die Lernreise konnte nicht gelöscht werden. Bitte versuche es erneut.",
    actionLabel: "Löschen",
    actionIcon: "trash",
  });

  // An anonymous identity has no `isAdmin` and can own nothing, so it is ruled
  // out before the ownership check below can ask.
  if (!me || me.isAnon) return null;

  // Only admins or the workshop's owner may delete it.
  if (!me.isAdmin && workshop.owner && workshop.owner.id !== me.id) return null;

  return (
    <>
      <Divider />

      <section className={styles.section}>
        <Text variant="subheader-small">Lernreise löschen</Text>

        <Text variant="body" color="neutral-100">
          Hier kannst du deine Lernreise löschen. Beachte, dass durch das
          Löschen der Lernreise alle Schüler*innen, die derzeit noch in der
          Lernreise sind, ihre Zugehörigkeit verlieren.
        </Text>

        <Button
          title="Lernreise löschen"
          variant="secondary"
          size="large"
          color="error"
          onClick={deleteWorkshop.open}
        />
        {deleteWorkshop.dialogElement}
      </section>
    </>
  );
};

/**
 * The workshop's options page: restarting the guided tour, the workshop's own
 * settings, and deleting the workshop.
 */
const OptionsOverview = ({ bag }: { bag: SparkBag }) => {
  const { workshop, settings } = bag;

  if (!canManageWorkshop(workshop.roles)) {
    return <NotAuthorizedPage />;
  }

  return (
    <PageShell
      surface="dark"
      titleSlot={<Text variant="h4">Optionen</Text>}
      titleButton={{ icon: "x", ariaLabel: "Schließen", to: ".." }}
    >
      <div className={styles.page}>
        <RestartTourButton workshop={workshop} />
        <SettingsSection workshop={workshop} settings={settings} />
        <DeleteSection workshop={workshop} />
      </div>
    </PageShell>
  );
};

export default OptionsOverview;
